import Axios, { AxiosInstance } from 'axios';
import { DocumentApi } from 'generated-api/bff';
import { DefaultApi } from 'generated-api/bff/api';
import { Configuration } from 'generated-api/bff/configuration';

type OptionalHeader = { name: string; value: string };

class AbAxiosSelfservice {
	private axios: AxiosInstance;
	public bffApiServiceLayer: DefaultApi; // API layer for BFFApi
	public documentApiServiceLayer: DocumentApi; // API layer for DocumentApi

	constructor(
		baseUrl: string,
		csrfToken: string,
		contentType = 'application/json',
		optionalHeaders: Array<OptionalHeader> = []
	) {
		this.axios = Axios.create({
			baseURL: baseUrl,
			timeout: 30000,
			headers: this.setupHeaders(contentType, csrfToken, optionalHeaders),
			withCredentials: true,
		});

		// Set up retry interceptor manually
		this.axios.interceptors.response.use(
			(response) => response,
			async (error) => {
				const { config } = error;
				if (!config.__retryCount) {
					config.__retryCount = 0;
				}

				// Retry up to 3 times for network-related errors
				if (config.__retryCount < 3 && (!error.response || error.response.status > 240)) {
					config.__retryCount += 1;
					await new Promise((resolve) => setTimeout(resolve, config.__retryCount * 200)); // Exponential backoff
					return this.axios(config);
				}

				return Promise.reject(error);
			}
		);

		// Initialize APIs with Configuration, using axios instance
		const configuration = new Configuration({ basePath: baseUrl });
		this.bffApiServiceLayer = new DefaultApi(configuration, baseUrl, this.axios);
		this.documentApiServiceLayer = new DocumentApi(configuration, baseUrl, this.axios);
	}

	private setupHeaders(
		contentType: string,
		csrfToken: string,
		optionalHeaders: Array<OptionalHeader>
	): Record<string, string> {
		const headers: Record<string, string> = {
			'x-csrf-token': csrfToken,
			'x-sales-channel': 'web',
			'Content-Type': contentType,
			Accept: 'application/json',
		};

		optionalHeaders.forEach(({ name, value }) => {
			headers[name] = value;
		});

		return headers;
	}
}

export const getApiInstance = async (endpoint: string = ''): Promise<AbAxiosSelfservice> => {
	const element = document.getElementById('app');
	const domainUrl = element?.getAttribute('data-domain') ?? '';

	const serviceUrl = ''; // Update with actual service URL if available
	const csrfToken = ''; // Update with actual CSRF token if available
	const baseUrl = domainUrl + '/api/v2/serviceproxy' + endpoint; // Update with actual base URL if available

	return new AbAxiosSelfservice(`${baseUrl}${serviceUrl}`, csrfToken);
};
